import { useState, useEffect, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import DOMPurify from "dompurify";
import { api, API } from "@/lib/api";
import { ArrowLeft, Newspaper, Loader2, ThumbsUp, MessageCircle, Share2, Check, Globe, Images, ZoomIn, Clock, Facebook, Twitter, Link2 } from "lucide-react";
import { Lightbox } from "@/components/Lightbox";
import InstagramEmbed from "@/components/InstagramEmbed";
import { Footer } from "@/components/landing/FooterCTA";
import { Navbar } from "@/components/landing/Navbar";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import logo from "../assets/logo.png";

const MONTHS = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
function fmtDate(d) {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt)) return null;
  return `${dt.getDate()} de ${MONTHS[dt.getMonth()]} de ${dt.getFullYear()}`;
}
function readingTime(html, excerpt) {
  const text = ((html || "").replace(/<[^>]+>/g, " ") + " " + (excerpt || "")).trim();
  const words = text ? text.split(/\s+/).length : 0;
  return Math.max(1, Math.round(words / 200));
}

export default function PostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [liked, setLiked] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/posts/by-slug/${slug}`)
      .then(({ data }) => setPost(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    api.get("/posts").then(({ data }) => setRelated((data || []).filter((p) => p.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug]);

  // Meta / título dinámico (navegador) + JSON-LD
  useEffect(() => {
    if (!post) return;
    const prevTitle = document.title;
    document.title = `${post.title} — Club Comandante Leo Nowak`;
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.setAttribute("data-post-ld", "1");
    ld.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "NewsArticle",
      headline: post.title, description: post.excerpt,
      image: post.share_image_url || post.cover_url || undefined,
      datePublished: post.created_at,
      author: { "@type": "Organization", name: "Club Comandante Leo Nowak" },
      publisher: { "@type": "Organization", name: "Club Comandante Leo Nowak" },
    });
    document.head.appendChild(ld);
    return () => { document.title = prevTitle; ld.remove(); };
  }, [post]);

  const shareUrl = `${API}/og/noticias/${slug}`;
  const shareTitle = post ? post.title : "Noticia del club";

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: shareTitle, text: shareTitle, url: shareUrl }); return; } catch { /* cancel */ }
    }
    copyLink();
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { window.prompt("Copiá el enlace:", shareUrl); }
  };
  const openShare = (url) => window.open(url, "_blank", "noopener,noreferrer,width=640,height=560");
  const shareWhatsApp = () => openShare(`https://wa.me/?text=${encodeURIComponent(`${shareTitle} ${shareUrl}`)}`);
  const shareFacebook = () => openShare(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`);
  const shareTwitter = () => openShare(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`);

  // Lightbox media = post images + album photos
  const postImages = (post?.images || []).map((u, i) => ({ url: u, alt: `${post?.title || "Foto"} ${i + 1}` }));
  const albumPhotos = post?.album?.photos || [];
  const media = [...postImages, ...albumPhotos];
  const nav = useCallback((dir) => {
    setLightbox((i) => (i === null ? i : (i + dir + media.length) % media.length));
  }, [media.length]);

  return (
    <div className="min-h-screen bg-[#e9ebf0] flex flex-col">
      <Navbar />
      <div className="pt-16 sm:pt-20">
        <Breadcrumbs items={[
          { label: "Noticias", to: "/noticias" },
          { label: post ? post.title : "Noticia" },
        ]} />
      </div>

      {loading ? (
        <div className="flex-1 py-24 flex justify-center text-navy/40"><Loader2 className="h-7 w-7 animate-spin" /></div>
      ) : notFound || !post ? (
        <div className="flex-1 py-24 text-center text-gray-400">
          <Newspaper className="h-10 w-10 mx-auto" />
          <p className="mt-3">No encontramos esta noticia.</p>
          <Link to="/noticias" className="mt-4 inline-block text-sm font-semibold text-navy underline">Ver todas las noticias</Link>
        </div>
      ) : (
        <main className="flex-1 w-full max-w-3xl mx-auto px-3 sm:px-6 py-6 sm:py-10">
          {/* Facebook-style post card */}
          <article data-testid="post-card" className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            {/* Post header */}
            <div className="flex items-center gap-3 p-4 sm:p-5">
              <div className="h-12 w-12 rounded-full bg-navy flex items-center justify-center overflow-hidden ring-2 ring-gold/40 shrink-0">
                <img src={logo} alt="Club" className="h-9 w-9 object-contain" />
              </div>
              <div className="min-w-0">
                <div className="font-bold text-navy leading-tight" style={{ fontFamily: "Outfit" }}>Club Comandante Leo Nowak</div>
                <div className="text-xs text-gray-400 flex items-center gap-1.5">
                  {fmtDate(post.date || post.created_at)} · <Globe className="h-3 w-3" /> · <Clock className="h-3 w-3" /> {readingTime(post.body_html, post.excerpt)} min
                </div>
              </div>
            </div>

            {/* Title + body */}
            <div className="px-4 sm:px-5 pb-3">
              <h1 data-testid="post-title" className="text-xl sm:text-2xl font-extrabold text-navy tracking-tight" style={{ fontFamily: "Outfit" }}>{post.title}</h1>
              {post.excerpt && <p className="mt-2 text-[15px] text-gray-600 font-medium">{post.excerpt}</p>}
              {(post.body_html || post.body) && (
                <div
                  className="post-content mt-3 text-gray-700"
                  data-testid="post-body"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.body_html || `<p>${(post.body || "").replace(/\n/g, "<br/>")}</p>`) }}
                />
              )}
            </div>

            {/* Cover image */}
            {post.cover_url && (
              <button onClick={() => media.length && setLightbox(0)} className={`block w-full ${media.length ? "cursor-zoom-in" : ""}`}>
                <img src={post.cover_url} alt={post.title} className="w-full max-h-[520px] object-cover" />
              </button>
            )}

            {/* Extra images grid */}
            {postImages.length > 0 && (
              <div className={`grid gap-1 p-1 ${postImages.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
                {postImages.map((m, i) => (
                  <button key={i} data-testid={`post-image-${i}`} onClick={() => setLightbox(i)} className="group relative aspect-[4/3] overflow-hidden cursor-zoom-in bg-navy/5">
                    <img src={m.url} alt={m.alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <span className="absolute inset-0 bg-navy/0 group-hover:bg-navy/20 transition-colors flex items-center justify-center">
                      <ZoomIn className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* Reaction bar */}
            <div className="px-4 sm:px-5 py-2 flex items-center justify-between text-xs text-gray-400 border-t border-gray-100 mt-1">
              <span className="inline-flex items-center gap-1"><span className="h-4 w-4 rounded-full bg-navy text-white inline-flex items-center justify-center"><ThumbsUp className="h-2.5 w-2.5" /></span> {liked ? "Vos y la hinchada" : "A la hinchada le gusta"}</span>
            </div>
            <div className="grid grid-cols-3 border-t border-gray-100 text-sm font-semibold text-gray-500">
              <button data-testid="post-like" onClick={() => setLiked((v) => !v)} className={`inline-flex items-center justify-center gap-2 py-2.5 hover:bg-gray-50 transition-colors ${liked ? "text-navy" : ""}`}>
                <ThumbsUp className={`h-5 w-5 ${liked ? "fill-navy" : ""}`} /> Me gusta
              </button>
              <a href="#comentar" className="inline-flex items-center justify-center gap-2 py-2.5 hover:bg-gray-50 transition-colors">
                <MessageCircle className="h-5 w-5" /> Comentar
              </a>
              <button data-testid="post-share" onClick={share} className="inline-flex items-center justify-center gap-2 py-2.5 hover:bg-gray-50 transition-colors">
                {copied ? <><Check className="h-5 w-5 text-navy" /> Copiado</> : <><Share2 className="h-5 w-5" /> Compartir</>}
              </button>
            </div>

            {/* Botones de compartir dedicados (usan la imagen destacada) */}
            <div className="px-4 sm:px-5 py-3 border-t border-gray-100 flex flex-wrap items-center gap-2" data-testid="post-share-bar">
              <span className="text-xs font-semibold text-gray-400 mr-1">Compartir:</span>
              <button onClick={shareWhatsApp} data-testid="share-whatsapp" title="Compartir por WhatsApp" className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-[#25D366] px-3 py-1.5 rounded-full hover:opacity-90 transition-opacity">
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </button>
              <button onClick={shareFacebook} data-testid="share-facebook" title="Compartir en Facebook" className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-[#1877F2] px-3 py-1.5 rounded-full hover:opacity-90 transition-opacity">
                <Facebook className="h-4 w-4" /> Facebook
              </button>
              <button onClick={shareTwitter} data-testid="share-twitter" title="Compartir en X" className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-black px-3 py-1.5 rounded-full hover:opacity-90 transition-opacity">
                <Twitter className="h-4 w-4" /> X
              </button>
              <button onClick={copyLink} data-testid="share-copy" title="Copiar enlace" className="inline-flex items-center gap-1.5 text-xs font-bold text-navy border border-navy/20 px-3 py-1.5 rounded-full hover:bg-navy/5 transition-colors">
                {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />} {copied ? "Copiado" : "Copiar"}
              </button>
            </div>
          </article>

          {/* Embedded gallery album */}
          {post.album && albumPhotos.length > 0 && (
            <section data-testid="post-album" className="mt-6 bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-navy font-bold" style={{ fontFamily: "Outfit" }}>
                  <Images className="h-5 w-5 text-gold" /> {post.album.name}
                </div>
                <Link to={`/galerias/${post.album.slug}`} className="text-sm font-semibold text-navy hover:underline">Ver álbum completo</Link>
              </div>
              {post.album.description && <p className="mt-1 text-sm text-gray-500">{post.album.description}</p>}
              <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-2">
                {albumPhotos.map((p, i) => (
                  <button
                    key={p.id}
                    data-testid={`post-album-photo-${i}`}
                    onClick={() => setLightbox(postImages.length + i)}
                    className="group relative aspect-square rounded-lg overflow-hidden cursor-zoom-in bg-navy/5"
                  >
                    <img src={p.url} alt={p.alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <span className="absolute inset-0 bg-navy/0 group-hover:bg-navy/25 transition-colors flex items-center justify-center">
                      <ZoomIn className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* Post de Instagram embebido */}
          {post.instagram_url && <InstagramEmbed url={post.instagram_url} />}

          {/* Noticias relacionadas */}
          {related.length > 0 && (
            <section data-testid="post-related" className="mt-6">
              <h2 className="text-lg font-bold text-navy mb-3" style={{ fontFamily: "Outfit" }}>Más noticias</h2>
              <div className="grid sm:grid-cols-3 gap-3">
                {related.map((p) => (
                  <Link key={p.id} to={`/noticias/${p.slug}`} data-testid={`related-${p.slug}`} className="group block bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                    <div className="aspect-[16/10] bg-navy/5 overflow-hidden">
                      {p.cover_url
                        ? <img src={p.cover_url} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                        : <div className="w-full h-full flex items-center justify-center text-navy/20"><Newspaper className="h-8 w-8" /></div>}
                    </div>
                    <div className="p-3">
                      <div className="text-[10px] font-semibold text-gold">{fmtDate(p.date || p.created_at)}</div>
                      <div className="text-sm font-bold text-navy leading-snug line-clamp-2">{p.title}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </main>
      )}

      <AnimatePresence>
        {lightbox !== null && media.length > 0 && (
          <Lightbox items={media} index={lightbox} onClose={() => setLightbox(null)} onNav={nav} />
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
